'use client';

import { useState } from 'react';
import { Badge } from '@workspace/ui/components/badge';
import { RatingBar } from './RatingBar';
import { RatingInput } from './RatingInput';
import { ReviewsFilter, StarsOption } from './ReviewsFilter';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { useRouter } from 'next/navigation';
import { ESystem, IProject } from 'types/src/types';
import { IReviewData } from '@/src/types/interfaces';
import { ReviewsList } from './ReviewsList';
import type { IReview, StarValue } from 'types/src/types';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Spinner } from '@/src/components/ui';

const PER_PAGE = 5;

function mergeUnique(prev: IReview[] | undefined, incoming: IReview[]) {
  const byId = new Map<number, IReview>();
  (prev ?? []).forEach((r) => byId.set(r.id, r));
  incoming.forEach((r) => {
    if (!byId.has(r.id)) byId.set(r.id, r);
  });
  return Array.from(byId.values());
}

type Props = {
  isProjectPage?: boolean;
  projectSlug?: string;
  reviewed?: boolean;
  downloaded?: boolean;
  project: IProject;
  reviewsData: IReviewData;
};

export const ReviewsSection = ({
  isProjectPage,
  projectSlug,
  reviewed,
  downloaded,
  project,
  reviewsData,
}: Props) => {
  const [currentFilter, setCurrentFilter] = useState<StarsOption>('all');
  const [state, setState] = useState<IReviewData>(reviewsData);
  const [locallyReviewed, setLocallyReviewed] = useState(false);
  const isLoadMore = useBoolean(false);
  const router = useRouter();
  const isReviewed =
    locallyReviewed || reviewed || project.isRatedByCurrentUser;
  const isDownloaded = downloaded || project.isDownloadedByCurrentUser;

  const hasMore = (state.reviews?.length ?? 0) < (state.totalReviews ?? 0);

  const handleFilterChange = async (rating: StarsOption) => {
    const ratingFilter = rating === 'all' ? undefined : String(rating);
    setCurrentFilter(rating);

    try {
      const params = new URLSearchParams({ page: '1', perPage: '5' });
      if (ratingFilter) params.set('ratingFilter', ratingFilter);

      const res = await fetch(
        `/api/projects/${project.id}/reviews?` + params.toString(),
        { method: 'GET', cache: 'no-store' },
      );
      if (!res.ok) throw new Error('Failed to fetch reviews');
      const data: IReviewData = await res.json();
      setState({
        reviews: mergeUnique([], data.reviews ?? []),
        breakdowns: data.breakdowns,
        averageRating: data.averageRating,
        totalReviews: data.totalReviews,
      });
    } catch (e) {
      console.error('Error fetching project reviews:', e);
    }
  };

  const handleReviewAdded = (newReview: IReview) => {
    const newReviews = [newReview, ...(state.reviews ?? [])];

    const r = newReview.rating as StarValue;
    const newBreakdowns = { ...(state.breakdowns ?? {}) };
    newBreakdowns[r] = (newBreakdowns[r] ?? 0) + 1;

    const newTotal = state.totalReviews + 1;
    const prevWeighted =
      1 * (state.breakdowns?.[1] ?? 0) +
      2 * (state.breakdowns?.[2] ?? 0) +
      3 * (state.breakdowns?.[3] ?? 0) +
      4 * (state.breakdowns?.[4] ?? 0) +
      5 * (state.breakdowns?.[5] ?? 0);
    const newWeighted = prevWeighted + r;
    const newAverage = newTotal > 0 ? newWeighted / newTotal : 0;

    setState({
      reviews: newReviews,
      breakdowns: newBreakdowns,
      averageRating: newAverage,
      totalReviews: newTotal,
    });

    setLocallyReviewed(true);
  };

  const handleLoadMore = async () => {
    if (isLoadMore.value || !hasMore) return;
    isLoadMore.onTrue();

    try {
      const ratingFilter =
        currentFilter === 'all' ? undefined : String(currentFilter);

      const currentCount = state.reviews?.length ?? 0;
      const nextPage = Math.floor(currentCount / PER_PAGE) + 1;

      const params = new URLSearchParams({
        page: String(nextPage),
        perPage: String(PER_PAGE),
      });
      if (ratingFilter) params.set('ratingFilter', ratingFilter);

      const res = await fetch(
        `/api/projects/${project.id}/reviews?` + params.toString(),
        { method: 'GET', cache: 'no-store' },
      );
      if (!res.ok) throw new Error('Failed to fetch more reviews');

      const data: IReviewData = await res.json();
      const newChunk = data.reviews ?? [];

      setState((prev) => {
        const merged = mergeUnique(prev.reviews, newChunk);
        return {
          reviews: merged,
          breakdowns: data.breakdowns ?? prev.breakdowns,
          averageRating: data.averageRating ?? prev.averageRating,
          totalReviews: data.totalReviews ?? prev.totalReviews,
        };
      });
    } catch (e) {
      console.error('Error loading more reviews:', e);
    } finally {
      isLoadMore.onFalse();
    }
  };

  const isMobileApp =
    project.systems.includes(ESystem.ANDROID) ||
    project.systems.includes(ESystem.IOS);

  return (
    <div>
      <div className="flex items-center justify-between w-full">
        <h1
          className={cn('text-[40px]! flex items-center gap-4', {
            'text-2xl!': isProjectPage,
          })}
        >
          Reviews{' '}
          <Badge
            variant="secondary"
            className="rounded-full font-light text-base w-11 h-7 bg-bg-gray"
          >
            {state.totalReviews}
          </Badge>
        </h1>

        {isProjectPage ? (
          <Button
            variant="ghost"
            className="hover:bg-transparent border-b border-black rounded-none text-sm! px-2"
            onClick={() => router.push(`/project/${projectSlug}/reviews`)}
          >
            All reviews
          </Button>
        ) : (
          <ReviewsFilter onFilterChange={handleFilterChange} />
        )}
      </div>

      <RatingBar
        averageRating={state.averageRating}
        totalReviews={state.totalReviews}
        breakdowns={state.breakdowns}
      />

      {state.reviews && !isProjectPage && (
        <>
          <ReviewsList reviews={state.reviews} />
          {hasMore && (
            <div className="flex justify-center mt-2 mb-6">
              <Button
                variant="ghost"
                className="rounded-full bg-bg-gray hover:bg-bg-dark-gray ease-in-out transition-colors duration-200 font-normal md:text-sm"
                onClick={handleLoadMore}
                disabled={isLoadMore.value}
              >
                {isLoadMore.value ? <Spinner /> : 'Show more'}
              </Button>
            </div>
          )}
        </>
      )}

      {((!isReviewed && isDownloaded) || isMobileApp) && (
        <RatingInput projectId={project.id} onReviewAdded={handleReviewAdded} />
      )}
    </div>
  );
};
